import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import TreeNode from "./TreeNode.jsx";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card.jsx";
import { Checkbox } from "./ui/checkbox.jsx";
import { ScrollArea } from "./ui/scroll-area.jsx";
import { Badge } from "./ui/badge.jsx";
import { X, Info, ChevronRight } from "lucide-react";
import { Label } from "./ui/label.jsx";
import { getListOfColumns } from "../../actions/inventoryBuilder.js";
import { getUniqueValues } from "../../actions/uniqueValues.js";
import styles from "./Styles/InventoryManagement.module.css";
import SaveTemplateModal from "./SaveTemplateModal.jsx";
import { toast } from "react-toastify";
import { saveTemplate } from "../../actions/saveTemplate.js";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumb } from "./ui/breadcrumb";
import { AppSidebar } from "./ui/sidebar";
import { fetchSchema } from "../../actions/schemaExplorer.js";
import { getListOfTables } from "../../actions/listOfTables.js";
import {
  toggleFavorite,
  fetchFavoriteInventory,
} from "../../actions/toggleFavorite.js";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import Header from "../header";
import { decryptData } from "../utils/EncriptionStore.js";

const InventoryManagement = () => {
  const user = (() => {
    const encryptedData = localStorage.getItem("profile");
    return encryptedData ? decryptData(encryptedData) : null;
  })();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const [inventory_name, setInventoryName] = useState(
    searchParams.get("inventory_name") || ""
  );

  useEffect(() => {
    dispatch(fetchSchema());
    dispatch(fetchFavoriteInventory());
  }, []);

  const schemasFromStore = useSelector((state) => state.schemaExplorer.schemas);
  const schemaFromURL = searchParams.get("schema");
  const tableFromURL = searchParams.get("table");
  const [selectedSchema, setSelectedSchema] = useState("");

  useEffect(() => {
    if (schemasFromStore.length > 0) {
      if (schemaFromURL && schemasFromStore.includes(schemaFromURL)) {
        setSelectedSchema(schemaFromURL);
      }
    }
  }, [schemasFromStore, schemaFromURL]);

  const [searchSchemaTerm, setSearchSchemaTerm] = useState("");

  useEffect(() => {
    if (selectedSchema) {
      setSearchSchemaTerm(selectedSchema);
    }
  }, [selectedSchema]);

  const [showSchemaDropdown, setShowSchemaDropdown] = useState(false);
  const schemaDropdownRef = useRef();

  const handleSchemaSelect = (schema) => {
    setSelectedSchema(schema);
    setSearchSchemaTerm(schema);
    setShowSchemaDropdown(false);
  };

  const filteredSchemas = schemasFromStore.filter((schema) =>
    schema.toLowerCase().includes(searchSchemaTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        schemaDropdownRef.current &&
        !schemaDropdownRef.current.contains(e.target)
      ) {
        setShowSchemaDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  console.log(selectedSchema, "Selected Schema");

  useEffect(() => {
    if (selectedSchema) {
      dispatch(getListOfTables({ schema: selectedSchema }));
    }
  }, [dispatch, selectedSchema]);

  const tablesFromStore = useSelector((state) => state.listOfTables.tables);
  console.log("Tables from store:", tablesFromStore);
  const [selectedTable, setSelectedTable] = useState("");

  useEffect(() => {
    if (tablesFromStore.length > 0) {
      if (tableFromURL && tablesFromStore.includes(tableFromURL)) {
        setSelectedTable(tableFromURL);
      }
    }
  }, [tablesFromStore, tableFromURL]);

  const [searchTableTerm, setSearchTableTerm] = useState("");

  useEffect(() => {
    if (selectedTable) {
      setSearchTableTerm(selectedTable);
    }
  }, [selectedTable]);

  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const tableDropdownRef = useRef();

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    setSearchTableTerm(table);
    setShowTableDropdown(false);
  };

  const filteredTables = tablesFromStore.filter((table) =>
    table.toLowerCase().includes(searchTableTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tableDropdownRef.current &&
        !tableDropdownRef.current.contains(e.target)
      ) {
        setShowTableDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const schema = selectedSchema || "";
  const table_name = selectedTable || "";

  const [treeData, setTreeData] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);

  const [selectionOrder, setSelectionOrder] = useState(0);

  const [selectedColumns, setSelectedColumns] = useState([]);

  const [loading, setLoading] = useState(false);

  const loadColumns = async () => {
    setLoading(true);
    try {
      const columns = await dispatch(getListOfColumns({ schema, table_name }));

      setAvailableColumns(
        columns.map((name) => ({
          name,
          selected: false,
        }))
      );
    } catch (error) {
      console.error("Error loading columns:", error);
      toast.info("Requires schema and table name to load columns.", {
        position: "top-right",
        autoClose: 3000,
      });
      setAvailableColumns([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load of columns
  useEffect(() => {
    loadColumns();
  }, [schema, table_name]);

  const showApplyToast = useCallback(
    debounce(() => {
      toast.info("Please click 'Apply Columns' to update the pivot tree.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        style: {
          background: "#ffffff",
          color: "green",
          fontWeight: "bold",
        },
      });
    }, 500),
    []
  );

  const handleColumnToggle = (column) => {
    setAvailableColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.name === column.name) {
          if (!col.selected) {
            // Selecting: set selectedAt timestamp
            return { ...col, selected: true, selectedAt: Date.now() };
          } else {
            // Deselecting: remove selectedAt
            return { ...col, selected: false, selectedAt: undefined };
          }
        }
        return col;
      })
    );
    showApplyToast();
  };

  const applyColumns = async () => {
    const columns = Array.isArray(availableColumns)
      ? availableColumns
          .filter((col) => col.selected)
          .sort((a, b) => a.selectedAt - b.selectedAt) // ensure order of selection
          .map((col) => col.name)
      : [];

    if (columns.length === 0) {
      toast.info("Please select at least one column to create the pivot tree.");
      setTreeData([]);
      setSelectedColumns([]);
      return;
    }

    setSelectedColumns(columns);

    setTreeData([]);

    if (columns.length > 0) {
      // Load root level data for the first column
      const rootColumn = columns[0];

      const params = {
        column: rootColumn,
        schema: schema,
        table: table_name,
        conditions: {},
        customer_id: user?.customer_id,
        group_id: user?.group_id,
        customer_name: user?.customer_name,
        group_name: user?.groupname,
      };

      setLoading(true);
      try {
        // Try to get values from the API
        const resultAction = await dispatch(getUniqueValues(params));

        const values = resultAction;

        const rootNodes = Array.isArray(values)
          ? values.map((value) => ({
              id: `${rootColumn}-${value}`,
              label: value,
              level: 0,
              expanded: false,
              column: rootColumn,
              value: value,
              children: columns.length > 1 ? [] : undefined,
              isLeaf: columns.length === 1,
            }))
          : [];
        setTreeData(rootNodes);
      } catch (error) {
        console.error("Failed to fetch root values:", error);

        setTreeData([]);
        toast.info("Could not fetch data from API. Using sample data instead.");
      } finally {
        setLoading(false);
      }
    }
  };

  const isSaveDisabled =
    loading || !Array.isArray(selectedColumns) || selectedColumns.length === 0;

  const handleSaveTemplate = async (template) => {
    const favoriteLabels = collectFavoriteLabels(treeData);
    const uniqueFavoriteLabels = [...new Set(favoriteLabels)];

    const inventoryKey =
      inventory_name && inventory_name.trim() !== ""
        ? inventory_name
        : template?.name || "";

    const payload = {
      [inventoryKey]: uniqueFavoriteLabels,
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const result = await dispatch(saveTemplate(template, navigate));

      // ✅ Only call favorite API if there are favorites
      if (favoriteLabels.length > 0) {
        try {
          const favoriteResult = await dispatch(toggleFavorite(payload));
          toast.success(
            favoriteResult?.content || "Favorites saved successfully!"
          );
        } catch (favoriteError) {
          toast.error(
            favoriteError?.message || "Failed to save favorites after template."
          );
        }
      }
    } catch (error) {
      toast.info(error || "Failed to save template.");
    }
  };

  const toggleNode = (nodeId) => {
    const toggleExpanded = (nodes) => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          const expanded = !node.expanded;

          // Load children if expanding and children are empty
          if (expanded && node.children && node.children.length === 0) {
            handleNodeExpand(nodeId, node);
          }

          // Return new node object with toggled expanded state
          return { ...node, expanded };
        }

        // If node has children, recursively toggle in children
        if (node.children) {
          const toggledChildren = toggleExpanded(node.children);

          // Only create new node object if children changed
          if (toggledChildren !== node.children) {
            return { ...node, children: toggledChildren };
          }
        }

        // No change for this node
        return node;
      });
    };

    setTreeData(toggleExpanded(treeData));
  };

  // Handle node selection
  const selectNode = (nodes, selectedId = null) => {
    return nodes.map((node) => {
      if (node.children) {
        return {
          ...node,
          selected: node.id === selectedId,
          children: selectNode(node.children, selectedId),
        };
      }
      return {
        ...node,
        selected: node.id === selectedId,
      };
    });
  };

  const handleSelectNode = useCallback((nodeId) => {
    setTreeData((prevTreeData) => {
      const newTreeData = selectNode(prevTreeData, nodeId);
      return newTreeData !== prevTreeData ? newTreeData : prevTreeData;
    });
  }, []);

  const collectConditions = (node, tree) => {
    if (!node) {
      return {};
    }

    const conditions = {};
    if (node.column && node.value) {
      conditions[node.column] = String(node.value);
    }

    const parent = findParentNode(tree, node.id);
    if (parent) {
      const parentConditions = collectConditions(parent, tree);
      return { ...parentConditions, ...conditions };
    }

    return conditions;
  };

  const findParentNode = (nodes, nodeId) => {
    for (const node of nodes) {
      if (node.children && node.children.some((child) => child.id === nodeId)) {
        return node;
      }
      if (node.children) {
        const foundInChildren = findParentNode(node.children, nodeId);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  };

  const handleNodeExpand = async (nodeId, node) => {
    // Skip if children are already loaded or not expandable
    if (node.children?.length > 0 || !node.column) return;

    // Find the current column index
    const nodeIndex = selectedColumns.indexOf(node.column);
    if (nodeIndex === -1 || nodeIndex >= selectedColumns.length - 1) return;

    const childColumn = selectedColumns[nodeIndex + 1];
    const conditions = collectConditions(node, treeData);

    const params = {
      column: childColumn,
      schema,
      table: table_name,
      ...(Object.keys(conditions).length > 0 && { conditions }),
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const resultAction = await dispatch(getUniqueValues(params));
      const values = resultAction;

      const childNodes = Array.isArray(values)
        ? values.map((value) => ({
            id: `${childColumn}-${value}-${nodeId}`,
            label: value,
            level: node.level + 1,
            expanded: false,
            column: childColumn,
            value,
            children:
              nodeIndex + 1 < selectedColumns.length - 1 ? [] : undefined,
            isLeaf: nodeIndex + 1 === selectedColumns.length - 1,
          }))
        : [];

      setTreeData((prevTree) =>
        updateTreeNodeChildren(prevTree, nodeId, childNodes)
      );
    } catch (error) {
      console.error("Failed to load child values:", error);
      setTreeData([]);
      toast.info("Could not fetch child values. Please try again.");
    }
  };

  const updateTreeNodeChildren = (tree, nodeId, childNodes) => {
    return tree.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          children: childNodes,
        };
      } else if (node.children) {
        return {
          ...node,
          children: updateTreeNodeChildren(node.children, nodeId, childNodes),
        };
      } else {
        return node;
      }
    });
  };

  const [isOpen, setIsOpen] = useState(true);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  const handleFavoriteToggle = (targetNode) => {
    // Helper to toggle a node and all its descendants
    const toggleAll = (node, newFavoriteState) => ({
      ...node,
      isFavorite: newFavoriteState,
      children: node.children
        ? node.children.map((child) => toggleAll(child, newFavoriteState))
        : node.children,
    });

    // Main function to traverse and update the tree
    const toggleFavorites = (nodes) =>
      nodes.map((node) => {
        if (node.id === targetNode.id) {
          const newFavoriteState = !node.isFavorite;
          return toggleAll(node, newFavoriteState);
        }
        if (node.children) {
          return {
            ...node,
            children: toggleFavorites(node.children),
          };
        }
        return node;
      });

    setTreeData((prevTree) => toggleFavorites(prevTree));
  };

  const collectFavoriteLabels = (nodes) =>
    nodes.flatMap((node) => {
      const labels = [];
      if (node.isFavorite && node.isLeaf) labels.push(String(node.label));
      if (node.children) labels.push(...collectFavoriteLabels(node.children));
      return labels;
    });

  const saveAsFavorite = async () => {
    const trimmedName = inventory_name?.trim();
    if (!trimmedName) {
      toast.info("Please save the inventory first before saving favorites.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      });
      return;
    }

    const favoriteLabels = collectFavoriteLabels(treeData);
    const uniqueFavoriteLabels = [...new Set(favoriteLabels)];
    const inventoryKey = trimmedName || "";

    const payload = {
      [inventoryKey]: uniqueFavoriteLabels,
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const resultAction = await dispatch(toggleFavorite(payload));
      toast.success(resultAction?.content || "Favorites saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      });
    } catch (error) {
      toast.error(error?.message || "Failed to save favorites.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      });
    }
  };

  const listOfFavorites = useSelector(
    (state) => state.toggleFavorite.favorites
  );

  return (
    <div className={styles.inventoryManagementContainer}>
      <div className={styles.twoSidebars}>
        <Header />
        <div
          className={` ${isOpen ? styles.sidebarOpen : styles.sidebarClosed}`}
        >
          <AppSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
        </div>
      </div>

      <div className={`${styles.dashboardLayout}`}>
        <div className={styles.schemaAndTableContainer}>
          <div className={styles.schemaAndTableSelectHeader}>
            <h1 className={styles.schemaAndTablesText}>
              Schema and Table Selection
            </h1>
          </div>

          <div className={styles.schemaAndTableSelectContainer}>
            <div
              className={styles.schemaSelectContainer}
              ref={schemaDropdownRef}
            >
              <label htmlFor="schema-input" className={styles.schemaLabel}>
                Select Schema:
              </label>
              <input
                type="text"
                id="schema-input"
                className={styles.schemaSelect}
                value={searchSchemaTerm}
                placeholder="Click Me To Search Schema..."
                onChange={(e) => {
                  setSearchSchemaTerm(e.target.value);
                  setShowSchemaDropdown(true);
                }}
                onFocus={() => setShowSchemaDropdown(true)}
              />
              {searchSchemaTerm && (
                <X
                  className={styles.clearIcon}
                  onClick={() => setSearchSchemaTerm("")}
                />
              )}
              {showSchemaDropdown && filteredSchemas.length > 0 && (
                <ul className={styles.dropdownList}>
                  {filteredSchemas.map((schema) => (
                    <li
                      key={schema}
                      className={styles.dropdownItem}
                      onClick={() => handleSchemaSelect(schema)}
                    >
                      {schema}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.tableSelectContainer} ref={tableDropdownRef}>
              <label htmlFor="table-input" className={styles.tableLabel}>
                Select Table:
              </label>
              <input
                type="text"
                id="table-input"
                className={styles.tableSelect}
                value={searchTableTerm}
                disabled={!selectedSchema}
                placeholder="Click Me To Search Table..."
                onChange={(e) => {
                  setSearchTableTerm(e.target.value);
                  setShowTableDropdown(true);
                }}
                onFocus={() => setShowTableDropdown(true)}
              />
              {searchTableTerm && (
                <X
                  className={styles.clearIcon}
                  onClick={() => setSearchTableTerm("")}
                />
              )}
              {showTableDropdown && filteredTables.length > 0 && (
                <ul className={styles.dropdownList}>
                  {filteredTables.map((table) => (
                    <li
                      key={table}
                      className={styles.dropdownItem}
                      onClick={() => handleTableSelect(table)}
                    >
                      {table}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className={styles.inventoryContainer}>
          <div className={styles.inventoryGrid}>
            <Card className={styles.inventoryCard}>
              <CardHeader className={styles.cardHeader}>
                <CardTitle className={styles.cardTitle}>
                  Select Columns
                </CardTitle>
                <CardDescription className={styles.cardDescription}>
                  Choose the columns to create your pivot hierarchy
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={styles.inventoryContent}>
                  <div className={styles.inventorySubContent}>
                    {Array.isArray(availableColumns) &&
                    availableColumns.length > 0 ? (
                      <ScrollArea
                        className={`${styles.scrollAreaHeight} ${styles.scrollAreaPaddingX} ${styles.scrollAreaPaddingY}`}
                      >
                        <div className={styles.availableColumnsContainer}>
                          {availableColumns
                            .slice() // shallow copy to avoid mutating original
                            .sort((a, b) => {
                              if (a.selected && b.selected) {
                                return (
                                  new Date(a.selectedAt) -
                                  new Date(b.selectedAt)
                                ); // sort selected by selectedAt
                              }
                              if (a.selected && !b.selected) return -1; // a before b
                              if (!a.selected && b.selected) return 1; // b before a
                              return a.name.localeCompare(b.name); // both unselected => alphabetical
                            })
                            .map((column) => (
                              <div
                                key={column.name}
                                className={styles.inventoryColumnItemContainer}
                              >
                                <Checkbox
                                  id={`column-${column.name}`}
                                  checked={column.selected}
                                  onCheckedChange={() =>
                                    handleColumnToggle(column)
                                  }
                                />
                                <Label
                                  htmlFor={`column-${column.name}`}
                                  className={`${styles.inventoryTextSmall} ${styles.inventoryFontMedium} ${styles.inventoryLeadingNone} ${styles.inventoryPeerDisabled}`}
                                >
                                  {column.name}
                                </Label>
                              </div>
                            ))}
                        </div>
                      </ScrollArea>
                    ) : (
                      <div className={styles.emptyStateContainer}>
                        <Info className={styles.emptyStateIcon} />
                        <div className={styles.inventoryTextCenter}>
                          <h3 className={styles.emptyStateTitle}>
                            No Columns To Display
                          </h3>
                          <p className={styles.emptyStateText}>
                            Select schema and table to load the columns.
                          </p>
                        </div>
                        {selectedColumns.length > 0 && (
                          <button
                            onClick={applyColumns}
                            className={`${styles.commonButton} ${styles.outlineButton} ${styles.refreshButton}`}
                          >
                            Refresh Data
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className={styles.inventoryTextMutedForeground}>
                      Selected columns (order determines hierarchy):
                    </Label>
                    <div className={styles.badgeContainer}>
                      {availableColumns
                        .filter((col) => col.selected)
                        .sort((a, b) => a.selectedAt - b.selectedAt) // ✅ sort by selection order
                        .map((column, index) => (
                          <Badge
                            key={column.name}
                            variant="secondary"
                            className={`${styles.badge} ${styles.inventoryColumnItemContainer}`}
                          >
                            <span>{index + 1}</span> <p>{column.name}</p>
                            <button
                              className={`${styles.commonButton} ${styles.inventoryMarginTop} ${styles.badgeButton}`}
                              onClick={() => handleColumnToggle(column)}
                            >
                              <X className={styles.inventorySizeTen} />
                            </button>
                          </Badge>
                        ))}
                    </div>
                  </div>
                  <div className={styles.inventoryButtonContainer}>
                    <button
                      onClick={applyColumns}
                      disabled={loading}
                      className={`${styles.commonButton} ${styles.applyButton}`}
                    >
                      {loading ? "Loading..." : "Apply Columns"}
                    </button>

                    <SaveTemplateModal
                      columns={availableColumns}
                      source_table={table_name}
                      schema={schema}
                      onSave={handleSaveTemplate}
                      trigger={
                        <button
                          disabled={isSaveDisabled}
                          className={`${styles.commonButton} ${styles.applyButton}`}
                        >
                          {loading ? "Loading..." : "Save Template"}
                        </button>
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={styles.inventoryCard}>
              <CardHeader className={styles.cardHeader}>
                <CardTitle className={styles.cardTitle}>Pivot Tree</CardTitle>
                <CardDescription className={styles.cardDescription}>
                  Explore your data in a hierarchical tree
                </CardDescription>
              </CardHeader>
              <CardContent>
                {treeData.length > 0 ? (
                  <div className={styles.inventoryContent}>
                    <div className={styles.inventorySubContent}>
                      <ScrollArea className={styles.scrollAreaHeight}>
                        <div className={styles.scrollAreaPaddingX}>
                          {treeData.map((node) => (
                            <TreeNode
                              key={node.id}
                              node={node}
                              onToggle={toggleNode}
                              onSelect={handleSelectNode}
                              onFavoriteToggle={handleFavoriteToggle}
                              listOfFavorites={listOfFavorites}
                              inventoryName={inventory_name}
                            />
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                    <div
                      className={`${styles.inventoryButtonContainer} ${styles.setFavoriteContainer}`}
                    >
                      {inventory_name && (
                        <button
                          onClick={saveAsFavorite}
                          disabled={loading}
                          className={`${styles.commonButton} ${styles.applyButton} ${styles.setFavoriteButton}`}
                        >
                          {loading ? "Loading..." : "Set Favorite"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyStateContainer}>
                    <Info className={styles.emptyStateIcon} />
                    <div className={styles.inventoryTextCenter}>
                      <h3 className={styles.emptyStateTitle}>
                        No Data to Display
                      </h3>
                      <p className={styles.emptyStateText}>
                        Select columns and apply them to generate the pivot tree
                      </p>
                    </div>
                    {selectedColumns.length > 0 && (
                      <button
                        onClick={applyColumns}
                        className={`${styles.commonButton} ${styles.outlineButton} ${styles.refreshButton}`}
                      >
                        Refresh Data
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to find a node by ID in the tree
const findNodeById = (nodes, nodeId) => {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children) {
      const found = findNodeById(node.children, nodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export default InventoryManagement;
